import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { fmtDuration } from "@/lib/time";
import { Button } from "@/components/ui/button";
import {
  Store, Users, Clock, AlertTriangle, TrendingUp, TrendingDown, Award, Activity, ArrowRight,
} from "lucide-react";

function Metric({ label, value, accent, icon: Icon, testId }) {
  return (
    <div className="bg-card border border-border p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="font-display text-5xl font-extrabold mt-2 leading-none" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

export default function CompanyDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/dashboard/company").then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <DashboardLayout title="Company Overview" needsStore={false}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Active Stores" value={`${data?.active_stores ?? "—"}/${data?.total_stores ?? "—"}`} accent="#fff" icon={Store} testId="metric-stores" />
        <Metric label="Customers Waiting" value={data?.total_waiting ?? "—"} accent="#F59E0B" icon={Clock} testId="metric-waiting" />
        <Metric label="Overdue" value={data?.total_overdue ?? "—"} accent="#FF3B30" icon={AlertTriangle} testId="metric-overdue" />
        <Metric label="Served Today" value={data?.total_served_today ?? "—"} accent="#22C55E" icon={Activity} />
        <Metric label="Company Avg Wait" value={fmtDuration(data?.company_avg_wait)} accent="#fff" icon={Clock} />
        <Metric label="Longest Active" value={fmtDuration(data?.longest_active_wait)} accent="#F59E0B" />
        <Metric label="Warehouse Agents" value={data?.warehouse_agents ?? "—"} accent="#3B82F6" icon={Users} />
        <Metric label="Front-Desk Agents" value={data?.frontdesk_agents ?? "—"} accent="#3B82F6" icon={Users} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border p-4 flex items-center gap-3">
          <Award size={22} className="text-alert-green" />
          <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Best Performing</div>
          <div className="font-display text-xl font-bold">{data?.best_store || "—"}</div></div>
        </div>
        <div className="bg-card border border-border p-4 flex items-center gap-3">
          <TrendingDown size={22} className="text-alert-amber" />
          <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Longest Avg Wait</div>
          <div className="font-display text-xl font-bold">{data?.worst_store || "—"}</div></div>
        </div>
        <div className="bg-card border border-border p-4 flex items-center gap-3">
          <AlertTriangle size={22} className="text-alert-red" />
          <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Most Overdue</div>
          <div className="font-display text-xl font-bold">{data?.most_overdue_store || "—"}</div></div>
        </div>
      </div>

      <div className="bg-card border border-border">
        <div className="px-4 py-3 border-b border-border font-display text-xl font-bold uppercase tracking-wide">All Locations</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="company-locations-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Store</th><th className="px-3 py-2">Waiting</th><th className="px-3 py-2">Preparing</th>
                <th className="px-3 py-2">Prepared</th><th className="px-3 py-2">In Service</th><th className="px-3 py-2">Overdue</th>
                <th className="px-3 py-2">Avg Wait</th><th className="px-3 py-2">Staff</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.stores || []).map((s) => (
                <tr key={s.store_id} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`loc-row-${s.store_code}`}>
                  <td className="px-3 py-3"><span className="font-semibold">{s.store_name}</span> <span className="font-mono-t text-xs text-muted-foreground">{s.store_code}</span></td>
                  <td className="px-3 py-3">{s.waiting}</td><td className="px-3 py-3">{s.preparing}</td>
                  <td className="px-3 py-3">{s.prepared}</td><td className="px-3 py-3">{s.in_service}</td>
                  <td className="px-3 py-3"><span className={s.overdue > 0 ? "text-alert-red font-bold" : ""}>{s.overdue}</span></td>
                  <td className="px-3 py-3">{fmtDuration(s.avg_wait_today)}</td>
                  <td className="px-3 py-3">{s.employees}</td>
                  <td className="px-3 py-3">{s.is_active ? <span className="text-alert-green text-xs uppercase">Active</span> : <span className="text-muted-foreground text-xs uppercase">Inactive</span>}</td>
                  <td className="px-3 py-3">
                    <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => navigate("/admin/reports")}>Reports <ArrowRight size={13} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
